import React, { useEffect, useState } from 'react';
import { Table, Tag, Typography, Alert, Card, Select } from 'antd';
import { AuditOutlined } from '@ant-design/icons';
import { getAuditTrail, getProjects } from '../services/api';
import { getApiError } from '../utils/apiError';
import RecordLink from '../components/RecordLink';

const { Title, Text } = Typography;

const eventTypeColor: { [key: string]: string } = {
  CREATE: 'green',
  UPDATE: 'blue',
  DELETE: 'red',
  STATUS_CHANGE: 'purple',
  LOGIN: 'cyan',
  LOGOUT: 'default',
  LOGIN_FAIL: 'red',
  FILE_UPLOAD: 'orange',
  FILE_VIEW: 'blue',
  FILE_DOWNLOAD: 'geekblue',
  FILE_ACKNOWLEDGE: 'green',
  EXPORT: 'purple',
  EMERGENCY_OVERRIDE: 'volcano',
  PERMISSION_CHANGE: 'magenta',
  DELEGATION_CREATE: 'cyan',
  DELEGATION_ACTIVATE: 'green',
  DELEGATION_EXPIRE: 'default',
};

interface Project {
  id: string;
  code: string;
  name: string;
}

const AuditTrailPage: React.FC = () => {
  const [auditLogs, setAuditLogs] = useState<any[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadProjects();
  }, []);

  useEffect(() => {
    loadAuditTrail(selectedProject);
  }, [selectedProject]);

  const loadProjects = async () => {
    try {
      const response = await getProjects();
      setProjects(response.data);
    } catch (err) {
      // Non-blocking: the trail still loads for all projects.
    }
  };

  const loadAuditTrail = async (projectId?: string) => {
    setLoading(true);
    setError('');
    try {
      const response = await getAuditTrail(projectId);
      setAuditLogs(response.data);
    } catch (err) {
      setError(getApiError(err, 'Failed to load audit trail.'));
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Event',
      dataIndex: 'event_type',
      key: 'event_type',
      render: (type: string) => (
        <Tag color={eventTypeColor[type] || 'default'}>
          {type.replace(/_/g, ' ')}
        </Tag>
      ),
    },
    {
      title: 'Record',
      key: 'record',
      render: (_: any, row: any) =>
        row.record_id ? (
          <RecordLink recordId={row.record_id} recordType={row.record_type} projectId={row.project_id} />
        ) : (
          <Text type="secondary">{row.entity_type}</Text>
        ),
    },
    {
      title: 'Type',
      key: 'record_type',
      render: (_: any, row: any) => <Text>{row.record_type || row.entity_type}</Text>,
    },
    {
      title: 'User',
      dataIndex: 'user',
      key: 'user',
      render: (user: any) => user ? user.full_name : '—',
    },
    {
      title: 'Before',
      dataIndex: 'before_state',
      key: 'before_state',
      ellipsis: true,
      render: (state: any) => state ? JSON.stringify(state) : '—',
    },
    {
      title: 'After',
      dataIndex: 'after_state',
      key: 'after_state',
      ellipsis: true,
      render: (state: any) => state ? JSON.stringify(state) : '—',
    },
    {
      title: 'IP Address',
      dataIndex: 'ip_address',
      key: 'ip_address',
      render: (ip: string) => ip || '—',
    },
    {
      title: 'Timestamp',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => new Date(date).toLocaleString('en-IN'),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <AuditOutlined style={{ fontSize: 24, color: '#1B2A4A' }} />
          <Title level={4} style={{ margin: 0, color: '#1B2A4A' }}>
            Audit Trail
          </Title>
        </div>
        <Select
          allowClear
          placeholder="All projects"
          value={selectedProject}
          onChange={(value) => setSelectedProject(value)}
          style={{ width: 250 }}
          options={projects.map(p => ({ value: p.id, label: `${p.code} — ${p.name}` }))}
        />
      </div>

      {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 24 }} />}

      <Card style={{ borderRadius: 12 }}>
        <Table
          columns={columns}
          dataSource={auditLogs}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 15 }}
          scroll={{ x: 1200 }}
          locale={{ emptyText: 'No audit trail records found.' }}
        />
      </Card>
    </div>
  );
};

export default AuditTrailPage;